import { Box, Collapse, Grid } from '@mui/material';
import { useState } from 'react';
import { ChevronDown, ChevronUp, Target } from '@sweep-io/sweep-design/dist/icons';
import { Radio, Tag, Typography, colors } from '@sweep-io/sweep-design';
import { StyledRadioGroup } from '../../common/StyledComponents';
import { CustomHeader } from '../../common/rule-builder/CustomHeader';
import { SweepBinaryRuleBuilder } from '../../common/sweep-condition-rule-builder/SweepConditionRuleBuilder';
import { useRefForMultipleRuleBuilders } from '../../common/sweep-condition-rule-builder/MultipleRuleBuildersContext';
import { StyledFormControlLabel, greyLayout, settingsPanelLayout } from './helpers';
import { SweepFieldOperator } from '../../SweepFieldsOperatorSelector/sweepFieldsTypesAndOperators';
import FieldLabel from '../../common/FieldLabel';
import { SFDCObjectType } from '../../../types/enums/SFDCObjectType';
import { SfdcDisplayType } from '../../../types/fields';
import { useSweepFieldsInfo } from '../../../sweep-fields/useSweepFieldsInfo';
import { SweepCriterion } from '../../../types/SweepCriterion';
import { SweepCondition } from '../../../types/SweepCondition';

interface MatchingLogicFilterPanelProps {
  matchingLogic?: SweepCondition;
  readonly?: boolean;
  crmOrgId: string;
  objectType: string;
  onChange: (filter?: SweepCondition) => any;
  referenceObjectType?: string;
  showOnlyRuleBuilder?: boolean;
  title?: string;
  subTitle?: string;
  infoTooltipTitle?: string;
  ruleBuilderTitle?: string;
  ruleBuilderHeaderLabel?: string;
}

enum SweepFilterType {
  HAS = 'HAS',
  EMPTY = 'EMPTY',
}

const FIELD_IDS_FOR_FUZZY_BY_SALESFORCE = ['Account.Name', 'Lead.Company', 'Lead.FirstName', 'Lead.LastName', 'Contact.FirstName', 'Contact.LastName'];

const MatchingLogicFilterPanel = ({
  readonly,
  crmOrgId,
  objectType,
  onChange,
  referenceObjectType,
  matchingLogic,
  showOnlyRuleBuilder,
  title,
  subTitle,
  infoTooltipTitle,
  ruleBuilderTitle,
  ruleBuilderHeaderLabel,
}: MatchingLogicFilterPanelProps) => {
  const [isPanelExpanded, setIsPanelExpanded] = useState(!!matchingLogic);
  const [selectFilterType, setSelectFilterType] = useState<SweepFilterType>(
    matchingLogic ? SweepFilterType.HAS : SweepFilterType.EMPTY,
  );
  const ruleBuilderRefInclude = useRefForMultipleRuleBuilders();

  const filterOperatorBy = (
    operatorList: OperatorWithIndex[],
    sfDisplayType?: SfdcDisplayType,
    fieldIds?: string[],
  ) => {
    const newOperators = [{ label: 'Exact', operator: SweepFieldOperator.EXACT, groupIndex: 0 }];

    if (sfDisplayType) {
      if (sfDisplayType === SfdcDisplayType.URL) {
        newOperators.push({ label: 'Fuzzy', operator: SweepFieldOperator.FUZZY, groupIndex: 0 });
      }

      if (
        fieldIds &&
        fieldIds.length > 0 &&
        FIELD_IDS_FOR_FUZZY_BY_SALESFORCE.includes(fieldIds[fieldIds.length - 1])
      ) {
        newOperators.push({
          label: 'Fuzzy',
          operator: SweepFieldOperator.FUZZY_BY_SALESFORCE,
          groupIndex: 0,
        });
      }

      if (sfDisplayType === SfdcDisplayType.DATE || sfDisplayType === SfdcDisplayType.DATETIME) {
        newOperators.push({
          label: 'Within X days of',
          operator: SweepFieldOperator.DATES_APART,
          groupIndex: 0,
        });
      }
      if (sfDisplayType === SfdcDisplayType.GEOLOCATION) {
        newOperators.push({
          label: 'Within X miles of',
          operator: SweepFieldOperator.DISTANCE,
          groupIndex: 0,
        });
      }
    }
    return newOperators;
  };

  const headerRowComponent = (
    <Box sx={{ display: 'flex', marginLeft: '36px', mb: '16px', pr: '36px' }}>
      <Grid container flexWrap="nowrap">
        <Grid item xs={4}>
          <Tag label={ruleBuilderHeaderLabel ?? 'Potential duplicate'} color={colors.grey[120]} />
        </Grid>
        <Grid item xs={3}></Grid>
        <Grid item xs={5}>
          <Tag label={`Triggering ${objectType?.toLowerCase()}`} color={colors.grey[120]} />
        </Grid>
      </Grid>
    </Box>
  );

  const { getSalesforceDisplayTypeFromFieldId } = useSweepFieldsInfo();

  const preFillOperators = (sweepCondition: SweepCondition) => {
    const conditionsWithOperator = sweepCondition?.criteria?.map((el) => {
      const sfDisplayType = getSalesforceDisplayTypeFromFieldId(el._fieldIds);
      if (
        sfDisplayType &&
        !el?.operator &&
        sfDisplayType !== SfdcDisplayType.URL &&
        sfDisplayType !== SfdcDisplayType.DATETIME &&
        sfDisplayType !== SfdcDisplayType.DATE &&
        sfDisplayType !== SfdcDisplayType.GEOLOCATION &&
        !(
          el?._fieldIds?.length > 0 &&
          FIELD_IDS_FOR_FUZZY_BY_SALESFORCE.includes(el._fieldIds[el._fieldIds.length - 1])
        )
      ) {
        el.operator = SweepFieldOperator.EXACT;
        return el;
      } else return el;
    });
    return { criteriaLogic: sweepCondition.criteriaLogic, criteria: conditionsWithOperator };
  };

  const ruleBuilderSection = (
    <Box sx={{ ...greyLayout }}>
      <>
        {!showOnlyRuleBuilder && (
          <CustomHeader
            customHeader={ruleBuilderTitle ?? 'Set your custom logic to find duplicates'}
          />
        )}
        <SweepBinaryRuleBuilder
          showCompoundGeolocation={
            objectType === SFDCObjectType.Opportunity &&
            referenceObjectType === SFDCObjectType.Opportunity
          }
          filterOperatorBy={filterOperatorBy}
          headerRowComponent={headerRowComponent}
          referenceObjectType={objectType}
          readOnly={readonly}
          crmOrgId={crmOrgId}
          objectType={referenceObjectType ?? ''}
          sweepCondition={matchingLogic}
          ref={ruleBuilderRefInclude}
          nestedSelectorFilterBy={(field) =>
            field.sfDisplayType !== SfdcDisplayType.LONGTEXTAREA || !field.sfField.calculated
          }
          onChange={(sweepCondition) => {
            const tempCondition = preFillOperators(sweepCondition);
            onChange(tempCondition);
          }}
          disableResolvePolymorphic
          onOperatorValueChange={(operatorValue: OperatorOffset, lineNumber: number) => {
            let tempLine = matchingLogic?.criteria?.[lineNumber - 1];
            const sfDisplayType = getSalesforceDisplayTypeFromFieldId(tempLine?._fieldIds);
            if (tempLine) {
              const _operator =
                sfDisplayType === SfdcDisplayType.GEOLOCATION
                  ? SweepFieldOperator.DISTANCE
                  : SweepFieldOperator.DATES_APART;
              tempLine = { ...tempLine, operator: _operator, operatorValue };
            }
            const objToSend = [...(matchingLogic?.criteria ?? [])];
            objToSend[lineNumber - 1] = tempLine as SweepCriterion;
            onChange({ criteriaLogic: matchingLogic?.criteriaLogic ?? '', criteria: objToSend });
          }}
        />
      </>
    </Box>
  );

  return (
    <Box sx={{ ...settingsPanelLayout }}>
      <Collapse in={isPanelExpanded} collapsedSize={24}>
        <Box className="panelHeader" onClick={() => setIsPanelExpanded(!isPanelExpanded)}>
          <Box sx={{ display: 'flex', gap: '16px' }}>
            <Target />
            <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: '2px' }}>
              <Typography variant="body-bold">{title ?? 'Duplicates logic'}</Typography>
              <Typography variant="body">
                {subTitle ?? 'Set the logic how to identify a potential duplicate'}
              </Typography>
            </Box>
          </Box>
          <Box>{isPanelExpanded ? <ChevronUp size="medium" /> : <ChevronDown size="medium" />}</Box>
        </Box>
        <Box sx={{ marginTop: '16px', marginLeft: '44px' }}>
          {showOnlyRuleBuilder ? (
            ruleBuilderSection
          ) : (
            <StyledRadioGroup
              sx={{ marginTop: '12px' }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const value = (event.target as HTMLInputElement).value as SweepFilterType;
                setSelectFilterType(value);
                if (value === SweepFilterType.EMPTY) {
                  onChange(undefined);
                }
              }}
              value={selectFilterType}
            >
              <StyledFormControlLabel
                disabled={readonly}
                value={SweepFilterType.EMPTY}
                control={<Radio />}
                label={
                  <FieldLabel
                    label={'Default Sweep logic'}
                    showTooltip
                    infoTooltipTitle={
                      infoTooltipTitle ??
                      'We leverage a combination of exact and fuzzy logic on standard fields like email, name, company, phone, and more to ensure an accurate detection of duplicates.'
                    }
                  />
                }
              />
              <StyledFormControlLabel
                disabled={readonly}
                value={SweepFilterType.HAS}
                control={<Radio />}
                label={<Typography variant="body">Custom logic</Typography>}
              />
              {selectFilterType === SweepFilterType.HAS && ruleBuilderSection}
            </StyledRadioGroup>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default MatchingLogicFilterPanel;
